import { useCallback, useLayoutEffect, useRef, useState } from 'react';

/**
 * Keeps an @ant-design/charts plot honest about the size of the box it lives in.
 *
 * Why this exists — the intermittent "chart spills out of its card" bug:
 *
 * g2plot decides the canvas size once, at construction, in
 * `Plot.getChartSize()`:
 *
 *     { width: width || containerSize.width || 400,
 *       height: height || containerSize.height || 400 }
 *
 * and `getContainerSize()` reads `clientWidth`/`clientHeight`, falling back to
 * `parseInt(getComputedStyle(el).width)`. If the chart's box happens to measure
 * 0 at the moment the React effect runs — the page still hidden behind the
 * loader, the sidebar mid-collapse, the antd Row/Col not laid out yet — that
 * expression yields NaN, NaN is falsy, and the chart silently renders itself at
 * a hardcoded **400 x 400**. A 400px-wide donut inside a ~280px card, or a
 * 400px-tall bar graph inside a 350px card, is exactly the overflow that shows
 * up "sometimes" and goes away on refresh: whether the box is measurable on
 * that first tick is a timing race, so a reload usually wins it.
 *
 * g2plot's own `autoFit` is supposed to repair this afterwards, but it only
 * repairs sizes it is told about — its size-sensor callback is debounced and
 * bound to that same element, and it never fires when the box was already at
 * its final size and only the *chart* was wrong.
 *
 * So this hook takes the measuring away from the library:
 *   - `ready` gates the chart's mount until the box has a real size, so the
 *     400x400 fallback branch is never reached in the first place;
 *   - a ResizeObserver drives `plot.changeSize()` for every later change, which
 *     covers the cases the library's sensor misses.
 *
 * Pair it with a box that has its own size from CSS (a `flex: 1` child, or an
 * explicit height) — never one that is sized by the chart it contains, or the
 * gate can never open.
 */
export default function useChartBox() {
  const boxRef = useRef(null);
  const plotRef = useRef(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  // Handed to the chart as `onReady` so we hold the live g2plot instance.
  // Stable identity on purpose: @ant-design/charts deep-compares the whole
  // config on every render and re-initialises the chart when it differs, so a
  // fresh function here would make the chart rebuild itself constantly.
  const onReady = useCallback((plot) => {
    plotRef.current = plot;
  }, []);

  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box) return undefined;

    let frame = null;

    const measure = () => {
      frame = null;
      const el = boxRef.current;
      if (!el) return;

      const width = Math.round(el.clientWidth);
      const height = Math.round(el.clientHeight);

      setSize((prev) => (prev.width === width && prev.height === height ? prev : { width, height }));

      // Resize the existing canvas in place. changeSize() is a no-op inside G2
      // when the numbers already match, so this costs nothing on the common path.
      const plot = plotRef.current;
      if (plot && width > 0 && height > 0 && plot.chart && !plot.chart.destroyed) {
        try {
          plot.changeSize(width, height);
        } catch (e) {
          // The chart can be torn down by its own effect between the observer
          // firing and this running; nothing to resize in that case.
        }
      }
    };

    // ResizeObserver can fire several times in one layout pass (and fires again
    // for the size change our own changeSize causes), so coalesce to one
    // measurement per frame.
    const schedule = () => {
      if (frame === null) {
        frame = requestAnimationFrame(measure);
      }
    };

    // Synchronous first read, before paint, so the chart mounts in this same
    // commit rather than flashing an empty box for a frame.
    measure();

    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(schedule);
    observer.observe(box);

    return () => {
      observer.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, []);

  return {
    boxRef,
    onReady,
    width: size.width,
    height: size.height,
    ready: size.width > 0 && size.height > 0,
  };
}
